import { useEffect, useState } from "react";
import { useReportByUUID } from "../../../api/ReportsApi";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import PDFDownloadWrapper from "../../../reports/PDFDownloadWrapper";
import { ReportRow } from "../../../reports/Table";
import { LazyLoadRowProps } from "../../../shared-components/LazyLoadTable";
import { formattedDate } from "../../../utils";
import ArrowDownTrayIcon from "../../../shared-components/icons/ArrowDownTrayIcon";
import InProgressIcon from "../../../shared-components/icons/InProgressIcon";
import StopwatchIcon from "../../../shared-components/icons/StopwatchIcon";
import XIcon from "../../../shared-components/icons/XIcon";
import { Link } from "react-router-dom";
import StatusAccent from "../../../shared-components/StatusAccent";
import { ReportSummaryItemApi } from "../../../api/Reports";
import {
  ReportEntryStyle,
  ReportEntry,
  ReportRowItem,
} from "../../../reports/helper-components/TableModels";
import {
  ProductReportStatus,
  SubscriptionRecurInstanceEx,
} from "../../../api/Person";

type SubscriptionRow = SubscriptionRecurInstanceEx & {
  showAllRows?: boolean;
};

function SubscriptionRowSkeleton({
  includeDownloadColumn,
}: {
  includeDownloadColumn: boolean;
}): JSX.Element {
  const columnCount = includeDownloadColumn ? 4 : 3;
  return (
    <div className="flex border-b text-xs leading-4 font-normal py-4">
      {Array.from({ length: columnCount }).map((_, i) => (
        <div key={i} className="flex px-3 items-center flex-1">
          <div className="w-3/4 skeleton__grey skeleton-small rounded" />
        </div>
      ))}
    </div>
  );
}

function buildStatusIcon(
  status: ProductReportStatus | null | undefined,
  report: ReportSummaryItemApi
): JSX.Element | null {
  if (status === "DONE") {
    // Keep DONE display identical to prior behavior (including StatusAccent on download).
    const accentReport = { ...report, report_status: "DONE" };
    return (
      <PDFDownloadWrapper report_uuid={report.report_uuid}>
        <div>
          <div className="flex relative">
            <div>
              <ArrowDownTrayIcon />
              <StatusAccent report={accentReport} />
            </div>
          </div>
        </div>
      </PDFDownloadWrapper>
    );
  }
  if (status === "IN_PROGRESS") {
    return (
      <div className="text-gray-500" title="In progress">
        <InProgressIcon height="20px" width="20px" />
      </div>
    );
  }
  if (status === "CANCEL") {
    return (
      <div className="text-gray-500" title="Cancelled">
        <XIcon />
      </div>
    );
  }
  return null;
}

function buildCompletedDateEntry(
  status: ProductReportStatus | null | undefined,
  report: ReportSummaryItemApi
): ReportEntry {
  if (status === "DONE") {
    return {
      text: report.finish_at,
      style: ReportEntryStyle.COMPONENT,
      component: (
        <Link
          className="underline"
          to={`/reports/${report.report_uuid}/summary`}
          target="_blank"
        >
          {formattedDate(report.finish_at)}
        </Link>
      ),
    };
  }

  if (!report.finish_at) {
    return { text: "", style: ReportEntryStyle.TEXT };
  }

  return {
    text: report.finish_at,
    style: ReportEntryStyle.COMPONENT,
    component: (
      <Link
        className="underline"
        to={`/reports/${report.report_uuid}/summary`}
        target="_blank"
      >
        {formattedDate(report.finish_at)}
      </Link>
    ),
  };
}

const SubscriptionLazyRow = ({
  parentRow,
  index,
}: LazyLoadRowProps<SubscriptionRow>): JSX.Element => {
  const {
    mutate: getReport,
    data: report,
    isLoading: loadingReport,
    isError: reportFetchFailed,
  } = useReportByUUID();
  const permissions = usePermissionValidator();
  const [row, setRow] = useState<ReportRowItem<ReportSummaryItemApi> | null>(
    null
  );

  const showAllRows = parentRow.showAllRows === true;
  const needsReportSummary =
    !!parentRow.request_uuid &&
    (showAllRows || parentRow.product_report_status === "DONE");

  // Fetch report summary only when this instance will be shown (DONE, or all=1).
  useEffect(() => {
    const key = parentRow.request_uuid;
    if (!key) return;
    if (!needsReportSummary) {
      setRow(null);
      return;
    }
    // Keep existing matching summary when toggling all=1; only fetch when needed.
    if (report && String(report.report_uuid) === String(key)) {
      return;
    }
    setRow(null);
    getReport(key);
  }, [parentRow.request_uuid, needsReportSummary, report, getReport]);

  // Scheduled future instance (no report yet).
  useEffect(() => {
    if (parentRow.request_uuid) return;
    if (!showAllRows) {
      setRow(null);
      return;
    }

    const scheduledRow: ReportRowItem<ReportSummaryItemApi> = {
      report_uuid: "",
      isLocked: false,
      data: [
        {
          text: formattedDate(parentRow.start_date),
          style: ReportEntryStyle.TEXT,
        },
        { text: "", style: ReportEntryStyle.TEXT },
        { text: "Not started yet", style: ReportEntryStyle.TEXT },
      ],
    };
    if (permissions.canDownloadPDF) {
      scheduledRow.data.push({
        text: "Scheduled",
        style: ReportEntryStyle.COMPONENT,
        component: (
          <div className="text-gray-500" title="Scheduled">
            <StopwatchIcon height="20px" width="20px" />
          </div>
        ),
      });
    }
    setRow(scheduledRow);
  }, [
    parentRow.request_uuid,
    showAllRows,
    parentRow.start_date,
    permissions.canDownloadPDF,
  ]);

  useEffect(() => {
    if (!parentRow.request_uuid || !report) return;
    // Ignore cached summary from a previous instance while this row component stays mounted.
    if (String(report.report_uuid) !== String(parentRow.request_uuid)) {
      return;
    }

    // Subscription page uses ProductReports status from /recur/subscription_instances,
    // not Reports.status from /report/summary.
    const status = parentRow.product_report_status;
    // Default mode: only completed (DONE) product reports.
    // Expanded all=1 mode: show every instance with a report, even if status is missing.
    if (!showAllRows && status !== "DONE") {
      setRow(null);
      return;
    }

    const statusIcon = buildStatusIcon(status, report);
    const newRow: ReportRowItem<ReportSummaryItemApi> = {
      report_uuid: report.report_uuid,
      report: report,
      isLocked: false,
      data: [
        buildCompletedDateEntry(status, report),
        {
          text: `${report.total_ratings ?? 0}`,
          style:
            status === "DONE"
              ? ReportEntryStyle.TOOLTIP
              : ReportEntryStyle.TEXT,
        },
        {
          text: report.report_uuid,
          style: ReportEntryStyle.TEXT,
        },
      ],
    };

    if (permissions.canDownloadPDF) {
      const iconLabel =
        status === "DONE"
          ? "Download PDF"
          : status === "IN_PROGRESS"
          ? "In progress"
          : status === "CANCEL"
          ? "Cancelled"
          : "";
      newRow.data.push({
        text: iconLabel,
        style: ReportEntryStyle.COMPONENT,
        component: statusIcon ?? <></>,
      });
    }
    setRow(newRow);
  }, [
    report,
    parentRow.request_uuid,
    parentRow.product_report_status,
    showAllRows,
    permissions.canDownloadPDF,
  ]);

  const reportMatchesParent =
    !!report &&
    !!parentRow.request_uuid &&
    String(report.report_uuid) === String(parentRow.request_uuid);

  // Skeleton only while a fetch is in flight (or before the first response).
  // On failure, fall through to an empty row instead of spinning forever.
  const showSkeleton =
    needsReportSummary &&
    !reportFetchFailed &&
    (loadingReport || !reportMatchesParent);

  if (showSkeleton) {
    return (
      <SubscriptionRowSkeleton
        includeDownloadColumn={permissions.canDownloadPDF}
      />
    );
  } else if (!row) {
    return <></>;
  } else {
    return (
      <ReportRow
        reportRow={row}
        index={index}
        key={parentRow.request_uuid ?? parentRow.start_date}
      />
    );
  }
};

export default SubscriptionLazyRow;
